import { Injectable } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Params, Router } from '@angular/router';
import { BehaviorSubject, filter, Observable, ReplaySubject, Subscription } from 'rxjs';

import { isMultiClient } from '../utils/client';

export interface DashboardModeAndId {
    mode: string | null;
    id: number | null;
}

@Injectable({ providedIn: 'root' })
export class CurrentStateService {
    public readonly isMultiClient$: Observable<boolean>;
    public readonly clientCode$: Observable<string>;
    public readonly dashboardModeAndId$: Observable<DashboardModeAndId>;
    public readonly currentWidgetId$: Observable<number | null>;

    private readonly currentWidgetId = new BehaviorSubject<number | null>(null);
    private readonly clientCode = new ReplaySubject<string>(1);
    private readonly isMultiClient = new ReplaySubject<boolean>(1);
    private readonly dashboardModeAndId = new BehaviorSubject<DashboardModeAndId>({ id: null, mode: null });
    private currentClientCode: string | null = null;
    private readonly routeSubscriptions: Subscription[] = [];

    constructor(private readonly route: ActivatedRoute, private readonly router: Router) {
        this.isMultiClient$ = this.isMultiClient.asObservable();
        this.clientCode$ = this.clientCode.asObservable();
        this.dashboardModeAndId$ = this.dashboardModeAndId.asObservable();
        this.currentWidgetId$ = this.currentWidgetId.asObservable();

        // the routing tree seems to be rebuilt on certain router events, so we'll just re-subscribe every time we navigate
        this.router.events
            .pipe(filter((event: Event) => event instanceof NavigationEnd))
            .subscribe(() => {
                this.routeSubscriptions.forEach((s) => s.unsubscribe());
                this.routeSubscriptions.splice(0);  // empty the array
                this.subscribe(this.route.root);
            });
    }

    private subscribe(route: ActivatedRoute): void {
        this.routeSubscriptions.push(route.params.subscribe(this.updateState.bind(this)));
        route.children.forEach((r) => this.subscribe(r));
    }

    private updateState(params: CurrentStateParams): void {
        if (params.clientCode != null) {
            const newClientCode = params.clientCode;

            // The whole app assumes it should completely reinit itself when "the client code changes"
            // Therefore, we can only emit a clientCode when it's actually changed
            if (newClientCode !== this.currentClientCode) {
                this.isMultiClient.next(isMultiClient(newClientCode));
                this.currentClientCode = newClientCode;
                this.clientCode.next(newClientCode);
            }
        }

        if (params.dashboardId != null) {
            const newId = params.dashboardId === 'none' ? null : Number(params.dashboardId);
            const newMode = params.mode ?? null;
            const currentValue = this.dashboardModeAndId.getValue();

            if (newId !== currentValue.id || newMode !== currentValue.mode) {
                this.dashboardModeAndId.next({
                    mode: newMode,
                    id: newId,
                });
            }
        }

        this.emitIfChanged(params);
    }

    private emitIfChanged(params: CurrentStateParams): void {
        if (params.coreWidgetId == null) {
            return;
        }

        const newCoreWidgetId = params.coreWidgetId === 'none' ? null : Number(params.coreWidgetId);

        if (newCoreWidgetId !== this.currentWidgetId.getValue()) {
            this.currentWidgetId.next(newCoreWidgetId);
        }
    }
}

interface CurrentStateParams extends Params {
    clientCode?: string;
    dashboardId?: string;
    mode?: string;
    coreWidgetId?: string;
}
